import * as React from 'react'

import { scrollNearestScrollable } from '../../util/scrollNearestScrollable'
import {
  useEngagementCoordinationStore,
  useEngagementCoordinationStoreApi,
} from '../../context/EngagementCoordinationContext'
import { selectNewlyArrivedIds } from './engagementCoordinationStore'
import { getNewEngagementDividerIndex } from './newEngagementBoundary'
import type { NewEngagementDirection } from './newEngagementBoundary'
import {
  clearPendingEngagementIds,
  markEngagementIdsSeen,
  useNewEngagementsRequestId,
  usePendingEngagementIds,
} from './newEngagementSignal'
import type { EngagementKind } from './newEngagementSignal'

type UseNewEngagementBoundaryParams = {
  /** Which inbox this list renders, so it only claims its own pending ids. */
  kind: EngagementKind
  /** The list's currently visible item ids (comments or mentions), in order. */
  itemIds: readonly string[]
  /**
   * Which end new items land on, driving where the divider sits and which way
   * the list scrolls to reveal them. @see NewEngagementDirection
   */
  direction: NewEngagementDirection
  /** The list container, used to scroll new items into view. */
  containerRef: React.RefObject<HTMLElement>
  /**
   * Identity of the list being rendered (channels + filters). When it changes
   * the boundary no longer applies, so anything still pending is forgotten.
   */
  resetKey: string
}

type NewEngagementBoundary = {
  /** Ids of items that arrived via the pill and sit beside the divider. */
  newlyArrivedIds: ReadonlySet<string>
  /** Index to render the divider at, or -1 when no divider should show. */
  dividerIndex: number
}

/**
 * Owns the list-side "new items" boundary shared by the comments and mentions
 * lists: it marks the items the user hasn't caught up with, tells the list
 * where to draw the divider, and scrolls them into view.
 *
 * Items are identified two ways, and the results are combined:
 *
 * - **By id**, against the ids reported by the ingestion Pusher events. This is
 *   exact wherever the item eventually loads, which is what makes oldest-first
 *   work: there the new items sort onto the last page, so they only appear once
 *   the user has paged down to them, mixed in with older items paged in along
 *   the way.
 * - **By diffing the list around a pill refetch**, for the leading run only.
 *   The channel-backfill event reports that new comments exist without saying
 *   which, so on that path there are no ids to match and this is the only way
 *   to spot them. It applies to top sorts, where a refetch of the first page is
 *   enough to surface them.
 */
export const useNewEngagementBoundary = ({
  kind,
  itemIds,
  direction,
  containerRef,
  resetKey,
}: UseNewEngagementBoundaryParams): NewEngagementBoundary => {
  const store = useEngagementCoordinationStoreApi()
  const requestId = useNewEngagementsRequestId()
  const pendingIds = usePendingEngagementIds(kind)

  // Always-current view of the visible ids, so the effects below read the
  // latest list without depending on a fresh array each render.
  const idsRef = React.useRef(itemIds)
  idsRef.current = itemIds
  const directionRef = React.useRef(direction)
  directionRef.current = direction

  const snapshotRef = React.useRef<ReadonlySet<string>>(new Set())
  const pendingRefetchRef = React.useRef(false)
  const lastRequestIdRef = React.useRef(requestId)

  const idsSignature = itemIds.join(',')

  // Claim any visible item the ingestion events flagged, and stop tracking it
  // as pending now that the user can see it. Runs whenever the list or the
  // pending set changes, so it catches items that only load pages later.
  React.useEffect(() => {
    if (pendingIds.size === 0) return
    const matched = idsRef.current.filter((id) => pendingIds.has(id))
    if (matched.length === 0) return
    store.getState().addNewlyArrived(matched)
    markEngagementIdsSeen(kind, matched)
    // idsSignature stands in for itemIds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsSignature, pendingIds, kind, store])

  // A pill click: scroll toward wherever the new items will show up, and take a
  // snapshot so the backfill path (which reports no ids) can still spot them.
  React.useEffect(() => {
    if (requestId === lastRequestIdRef.current) return
    lastRequestIdRef.current = requestId
    scrollNearestScrollable(containerRef.current, directionRef.current)
    if (directionRef.current === 'top') {
      snapshotRef.current = new Set(idsRef.current)
      pendingRefetchRef.current = true
    }
  }, [requestId, containerRef])

  // The first list change after that click is the refetch landing. Mark the
  // leading run of items that weren't there before, then stop treating the
  // request as pending: including when it added nothing, so a later unrelated
  // change can't be attributed to it.
  React.useEffect(() => {
    if (!pendingRefetchRef.current) return
    pendingRefetchRef.current = false
    const snapshot = snapshotRef.current
    const added: string[] = []
    for (const id of idsRef.current) {
      if (snapshot.has(id)) break
      added.push(id)
    }
    if (added.length > 0) {
      store.getState().addNewlyArrived(added)
    }
    // idsSignature stands in for itemIds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsSignature, store])

  // A different list is on screen, so nothing carried over still applies. The
  // per-list marks reset with the coordination store; the pending ids are
  // process-wide, so clear them here too.
  const isFirstResetKey = React.useRef(true)
  React.useEffect(() => {
    if (isFirstResetKey.current) {
      isFirstResetKey.current = false
      return
    }
    clearPendingEngagementIds(kind)
  }, [resetKey, kind])

  const newlyArrivedIds = useEngagementCoordinationStore(selectNewlyArrivedIds)

  // Bring the new items into view once they're actually on screen.
  const previousNewCount = React.useRef(newlyArrivedIds.size)
  React.useEffect(() => {
    if (newlyArrivedIds.size > previousNewCount.current) {
      scrollNearestScrollable(containerRef.current, direction)
    }
    previousNewCount.current = newlyArrivedIds.size
  }, [newlyArrivedIds, containerRef, direction])

  return {
    newlyArrivedIds,
    dividerIndex: getNewEngagementDividerIndex(
      itemIds,
      newlyArrivedIds,
      direction,
    ),
  }
}
